/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  Table,
  Badge,
  Avatar,
  Group,
  Text,
  Select,
  TextInput,
  ActionIcon,
  Pagination,
  Checkbox,
  Stack,
  Center,
  Paper,
  ScrollArea,
  Loader,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconChevronDown,
} from "@tabler/icons-react";
import { useGetLeaveRequests } from "api/newLeave/getLeaveRequest";
import { useGetLeaveBalance } from "api/newLeave/getLeavebalance";
import CancelLeave from "../cancelLeave";
import { set } from "zod";
import { PermissionActionIcon } from "api/permission/permissionActionBtn";
import { formatDateToDDMMYYYY } from "utils/dateFormate";
import EditleaveModal from "../editleave";
import DeleteApprovedLeaveModal from "../cancelLeave/deleteApprovedLeave";

// Format number to always show 2 decimal places for consistency
const formatDays = (num: number): string => {
  if (num === null || num === undefined || isNaN(num)) return "0.00";
  return num.toFixed(2);
};

type Approver = { name: string; role: string; avatar?: string };

type Leave = {
  id: string;
  leaveType: string;
  from: string; // display string
  to: string; // display string
  rawFrom?: string | null; // ISO for filtering
  rawTo?: string | null; // ISO for filtering
  approvedBy: Approver;
  noOfDays: string;
  status: string;
  totalDays: number;
  paidDays: number;
  unpaidDays: number;
  hasUnpaid: boolean;
};

function formatDateISO(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const day = d.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(day).padStart(2, "0")} ${monthNames[d.getMonth()]
    } ${d.getFullYear()}`;
}

function calcDaysInclusive(fromIso?: string | null, toIso?: string | null) {
  try {
    if (!fromIso) return 1;
    const from = new Date(fromIso);
    const to = toIso ? new Date(toIso) : from;
    if (Number.isNaN(from.getTime())) return 1;
    if (Number.isNaN(to.getTime())) return 1;
    const msPerDay = 24 * 60 * 60 * 1000;
    let diff =
      Math.round(
        (to.setHours(0, 0, 0, 0) - from.setHours(0, 0, 0, 0)) / msPerDay
      ) + 1;
    if (diff < 1) diff = 1;
    return diff;
  } catch (e) {
    return 1;
  }
}

function mapStatus(apiStatus?: string) {
  if (!apiStatus) return "-";
  const lower = apiStatus.toLowerCase();
  if (lower === "approved") return "Approved";
  if (lower === "pending") return "Pending";
  if (lower === "rejected" || lower === "declined") return "Rejected";
  return apiStatus.charAt(0).toUpperCase() + apiStatus.slice(1);
}

function parseApiArray(maybe: unknown): any[] {
  if (!maybe) return [];
  if (Array.isArray(maybe)) return maybe;
  if (typeof maybe === "object" && maybe !== null) {
    const obj = maybe as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as any[];
  }
  return [];
}

function getApproverDisplay(approval: any, fallbackId: string): Approver {
  const approverObj = approval?.approver;

  const firstName = approverObj?.firstName || approval?.firstName || "";
  const lastName = approverObj?.lastName || approval?.lastName || "";
  let name = `${firstName} ${lastName}`.trim();

  if (!name) name = "Unknown";

  const role = approval?.role || approverObj?.role || "-";

  const avatar =
    approverObj?.avatar ||
    approverObj?.photo ||
    `https://i.pravatar.cc/40?u=${approverObj?._id ?? fallbackId}`;

  return { name, role, avatar };
}

export default function LeaveList({ userId }: { userId?: string }) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const resp = useGetLeaveRequests({
    year: selectedYear,
    page,
    limit: perPage,
    userId,
  } as any);

  const { data } = useGetLeaveBalance(userId);
  const [opened, setOpened] = useState({
    id: "",
    isOpen: false,
  });
  const [openededit, setOpenedEdit] = useState({
    id: "",
    isOpen: false,
  });
  const [openedDeleteApproved, setOpenedDeleteApproved] = useState({
    id: "",
    isOpen: false,
  });
  console.log(openededit.id, " Leave ID for edit modal");
  const payload = data?.data;
  const isLoading = (resp as any)?.isLoading ?? false;
  const rawBody = (resp as any)?.data ?? (resp as any);
  const apiArray = useMemo(() => parseApiArray(rawBody), [rawBody]);

  // Get pagination info from response
  const totalRecords = rawBody?.total ?? apiArray.length;
  const totalPages = rawBody?.totalPages ?? Math.ceil(totalRecords / perPage);
  console.log('payload 1 ', payload);

  const totalRemaining = useMemo(() => {
    if (!payload || typeof payload !== "object") return 0;
    let sum = 0;
    Object.keys(payload).forEach((k) => {
      console.log('payload 2 ', k);

      if (k === "leaveWithoutPay") return;
      try {
        const v = (payload as any)[k];
        if (v && typeof v === "object" && v.hasOwnProperty("balance")) {
          const bal = Number(v.balance);
          if (!Number.isNaN(bal)) sum += bal;
        }
      } catch (e) { }
    });
    return sum;
  }, [payload]);

  const mappedLeaves: Leave[] = useMemo(() => {
    return apiArray.map((item: any) => {
      const id = item._id ?? item.id ?? Math.random().toString(36).slice(2, 9);
      const leaveTypeRaw = item.leaveType ?? "Unknown";
      const leaveTypeLabel =
        leaveTypeRaw === "annual"
          ? "Annual Leave"
          : leaveTypeRaw === "sick"
            ? "Sick Leave"
            : leaveTypeRaw.charAt(0).toUpperCase() + leaveTypeRaw.slice(1);

      const fromIso = item.fromDate ?? item.from ?? item.from_date ?? null;
      const toIso = item.toDate ?? item.to ?? item.to_date ?? fromIso;

      let approvedBy: Approver = {
        name: "No Approvals Yet",
        role: "-",
        avatar: `https://i.pravatar.cc/40?u=${id}`,
      };

      if (Array.isArray(item.approvals) && item.approvals.length > 0) {
        const completedApprovals = item.approvals.filter(
          (approval: any) =>
            approval.status === "approved" || approval.status === "rejected"
        );

        if (completedApprovals.length > 0) {
          const last = completedApprovals[completedApprovals.length - 1];
          approvedBy = getApproverDisplay(last, last?.approver?._id ?? id);
        }
      }

      // compute days as numeric
      const daysNum =
        (typeof item.numberOfDays === "number" && item.numberOfDays) ||
        Number(item.numberOfDays) ||
        calcDaysInclusive(fromIso ?? null, toIso ?? null);

      const totalDays = Number(daysNum) || 0;
      const unpaidDays = Number(item.unpaidDays) || 0;
      const paidDays = totalDays - unpaidDays;
      const hasUnpaid = unpaidDays > 0;

      return {
        id,
        leaveType: leaveTypeLabel,
        from: formatDateISO(fromIso), // display
        to: formatDateISO(toIso), // display
        rawFrom: fromIso ?? null,
        rawTo: toIso ?? fromIso ?? null,
        approvedBy,
        noOfDays: `${formatDays(daysNum)} Day${daysNum > 1 ? "s" : ""}`,
        status: mapStatus(item.status),
        totalDays,
        paidDays,
        unpaidDays,
        hasUnpaid,
      } as Leave;
    });
  }, [apiArray]);

  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedApprover, setSelectedApprover] = useState<string | null>(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState<string | null>(
    null
  );
  const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(
    null
  );
  const [dateRange, setDateRange] = useState<
    [Date | null, Date | null] | undefined
  >(undefined);
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  const approvers = useMemo(() => {
    const unique = Array.from(
      new Map(
        mappedLeaves.map((d) => [d.approvedBy.name, d.approvedBy])
      ).values()
    );
    return unique.map((a) => ({
      value: a.name,
      label: `${a.name} • ${a.role}`,
    }));
  }, [mappedLeaves]);

  const leaveTypes = useMemo(() => {
    return Array.from(new Set(mappedLeaves.map((d) => d.leaveType))).map(
      (t) => ({ value: t, label: t })
    );
  }, [mappedLeaves]);

  const statuses = useMemo(() => {
    return Array.from(new Set(mappedLeaves.map((d) => d.status))).filter(
      (s) => s && s !== "-"
    );
  }, [mappedLeaves]);

  // Client-side filtering (on the current page data)
  const filtered = useMemo(() => {
    return mappedLeaves
      .filter((r) => (selectedStatus ? r.status === selectedStatus : true))
      .filter((r) =>
        selectedApprover ? r.approvedBy.name === selectedApprover : true
      )
      .filter((r) =>
        selectedLeaveType ? r.leaveType === selectedLeaveType : true
      )
      .filter((r) => {
        if (!selectedPaymentType) return true;
        if (selectedPaymentType === "paid") return !r.hasUnpaid;
        if (selectedPaymentType === "unpaid") return r.hasUnpaid;
        if (selectedPaymentType === "fully-unpaid")
          return r.paidDays === 0 && r.unpaidDays > 0;
        return true;
      })
      .filter((r) =>
        search
          ? [r.leaveType, r.from, r.to, r.approvedBy.name, r.noOfDays]
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase())
          : true
      )
      .filter((r) => {
        // Date range filter (inclusive overlap): require both start and end selected
        if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
        const start = new Date(dateRange[0]);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange[1]);
        end.setHours(23, 59, 59, 999);

        try {
          const lf = r.rawFrom ? new Date(r.rawFrom) : null;
          const lt = r.rawTo ? new Date(r.rawTo) : lf;
          if (!lf || Number.isNaN(lf.getTime())) return false;
          if (!lt || Number.isNaN(lt.getTime())) return false;

          // overlap test: leaveFrom <= end && leaveTo >= start
          if (lf <= end && lt >= start) return true;
          return false;
        } catch (e) {
          return false;
        }
      });
  }, [
    mappedLeaves,
    selectedStatus,
    selectedApprover,
    selectedLeaveType,
    selectedPaymentType,
    search,
    dateRange,
  ]);

  // For display - use filtered data when using client filters, otherwise show all from backend
  const displayData = filtered;

  function statusBadge(status: string) {
    const lower = status.toLowerCase();

    if (lower === "approved")
      return (
        <Badge color="green" variant="filled">
          Approved
        </Badge>
      );
    if (lower === "declined" || lower === "rejected")
      return (
        <Badge color="red" variant="filled">
          Rejected
        </Badge>
      );
    if (lower === "pending")
      return (
        <Badge color="yellow" variant="filled">
          Pending
        </Badge>
      );

    return (
      <Badge color="gray" variant="light">
        {status}
      </Badge>
    );
  }

  return (
    <Paper shadow="sm" radius="md" p="lg" withBorder>
      <Stack spacing="md">
        <Group position="apart" align="center">
          <Group spacing="xs" align="center">
            <Text weight={600} size="lg">
              Leave List
            </Text>
            <Group spacing={6}>
              <Badge color="blue" variant="light" size="md">
                Total Requests: {totalRecords}
              </Badge>
              <Badge color="green" variant="light" size="md">
                Remaining Balance: {totalRemaining.toFixed(2)} days
              </Badge>
            </Group>
          </Group>

          <Group spacing="sm" align="center">
            <Group align="center" spacing="xs">
              <Select
                label="Year"
                data={[
                  { value: "2024", label: "2024" },
                  { value: "2025", label: "2025" },
                  { value: "2026", label: "2026" },
                  { value: "2027", label: "2027" },
                ]}
                value={String(selectedYear)}
                onChange={(v) => {
                  setSelectedYear(Number(v) || new Date().getFullYear());
                  setPage(1); // Reset to first page when changing year
                }}
              />
            </Group>

            <Group align="center" spacing="xs">
              <Select
                label="Rows Per Page"
                data={[
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                  { value: "100", label: "100" },
                ]}
                value={String(perPage)}
                onChange={(v) => {
                  setPerPage(Number(v) || 10);
                  setPage(1); // Reset to first page when changing rows per page
                }}
              />
            </Group>

            <DateRangePicker
              label="Date Range"
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                setPage(1);
              }}
              placeholder="Select date range"
              icon={<IconChevronDown size={14} />}
              clearable
            />

            <Select
              label="Leave Type"
              placeholder="All"
              data={[{ value: "", label: "All" }, ...leaveTypes]}
              value={selectedLeaveType ?? ""}
              onChange={(v) => setSelectedLeaveType(v || null)}
            />

            <Select
              label="Payment Type"
              placeholder="All"
              data={[
                { value: "", label: "All" },
                { value: "paid", label: "Fully Paid" },
                { value: "unpaid", label: "Has Unpaid" },
                { value: "fully-unpaid", label: "Fully Unpaid" },
              ]}
              value={selectedPaymentType ?? ""}
              onChange={(v) => setSelectedPaymentType(v || null)}
            />

            <Select
              label="Status"
              placeholder="All"
              data={[
                { value: "", label: "All" },
                ...statuses.map((s) => ({ value: s, label: s })),
              ]}
              value={selectedStatus ?? ""}
              onChange={(v) => setSelectedStatus(v || null)}
            />

            <TextInput
              label="Search"
              placeholder="Search"
              icon={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </Group>
        </Group>

        <Group position="apart" align="center"></Group>

        <ScrollArea>
          {isLoading ? (
            <Center style={{ height: 160 }}>
              <Loader />
            </Center>
          ) : (
            <Table verticalSpacing="sm" highlightOnHover>
              <thead
                style={{
                  backgroundColor: "#f5f5f5",
                }}
              >
                <tr>
                  {/* <th style={{ width: 40 }}>
                    <Checkbox
                      checked={
                        Object.values(selectedRows).every(Boolean) &&
                        Object.values(selectedRows).length > 0
                      }
                      onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        if (checked) {
                          const all: Record<string, boolean> = {};
                          displayData.forEach((r) => (all[r.id] = true));
                          setSelectedRows((s) => ({ ...s, ...all }));
                        } else {
                          const copy = { ...selectedRows };
                          displayData.forEach((r) => delete copy[r.id]);
                          setSelectedRows(copy);
                        }
                      }}
                    />
                  </th> */}
                  <th>Leave Type</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Days</th>
                  <th>Paid/Unpaid</th>
                  <th>Status</th>
                  <th>Approved/Reviewed By</th>
                  <th style={{ width: 100, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((row) => (
                  <tr key={row.id}>
                    {/* <td>
                      <Checkbox
                        checked={!!selectedRows[row.id]}
                        onChange={(e) =>
                          setSelectedRows((s) => ({
                            ...s,
                            [row.id]: e.currentTarget.checked,
                          }))
                        }
                      />
                    </td> */}
                    <td>
                      <Text size="sm" weight={500}>
                        {row.leaveType}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm">
                        {row.rawFrom
                          ? formatDateToDDMMYYYY(row.rawFrom)
                          : row.from ?? "N/A"}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm">
                        {row.rawTo
                          ? formatDateToDDMMYYYY(row.rawTo)
                          : row.to ?? "N/A"}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm" weight={500}>
                        {row.noOfDays}
                      </Text>
                    </td>
                    <td>
                      {row.hasUnpaid ? (
                        <Stack spacing={2}>
                          <Group spacing={4}>
                            <Badge size="xs" color="green" variant="light">
                              Paid: {formatDays(row.paidDays)}
                            </Badge>
                          </Group>
                          <Group spacing={4}>
                            <Badge size="xs" color="orange" variant="filled">
                              Unpaid: {formatDays(row.unpaidDays)}
                            </Badge>
                          </Group>
                        </Stack>
                      ) : (
                        <Badge size="sm" color="green" variant="light">
                          Fully Paid
                        </Badge>
                      )}
                    </td>
                    <td>{statusBadge(row.status)}</td>
                    <td>
                      <div>
                        <Text size="sm" weight={600}>
                          {row.approvedBy.name}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {row.approvedBy.role}
                        </Text>
                      </div>
                    </td>
                    <td>
                      <Group position="center" spacing="xs">
                        <PermissionActionIcon
                          permission="employees.employeeleaveEdit"
                          variant="transparent"
                          onClick={() => {
                            setOpenedEdit({
                              id: row.id,
                              isOpen: true,
                            });
                          }}
                        >
                          <IconEdit size={16} />
                        </PermissionActionIcon>

                        {row.status.toLowerCase() === "pending" && (
                          <PermissionActionIcon
                            permission="employees.employeeleaveEdit"
                            variant="transparent"
                            onClick={() => {
                              setOpened({
                                id: row.id,
                                isOpen: true,
                              });
                            }}
                          >
                            <IconTrash size={16} />
                          </PermissionActionIcon>
                        )}

                        {row.status.toLowerCase() === "approved" && (
                          <PermissionActionIcon
                            permission="employees.employeeleaveEdit"
                            variant="transparent"
                            onClick={() => {
                              setOpenedDeleteApproved({
                                id: row.id,
                                isOpen: true,
                              });
                            }}
                          >
                            <IconTrash size={16} color="red" />
                          </PermissionActionIcon>
                        )}
                      </Group>
                    </td>
                  </tr>
                ))}

                {displayData.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <Center style={{ height: 120 }}>
                        <Text color="dimmed">No entries</Text>
                      </Center>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </ScrollArea>

        <Group position="apart" align="center">
          <Text size="sm">
            Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} -{" "}
            {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
          </Text>
          <Pagination
            page={page}
            onChange={setPage}
            total={Math.max(1, totalPages)}
          />
        </Group>
      </Stack>
      <CancelLeave
        opened={opened.isOpen}
        onClose={() =>
          setOpened({
            id: "",
            isOpen: false,
          })
        }
        leaveId={opened.id}
      />
      <EditleaveModal
        opened={openededit.isOpen}
        onClose={() =>
          setOpenedEdit({
            id: "",
            isOpen: false,
          })
        }
        leaveId={openededit.id}
      />
      <DeleteApprovedLeaveModal
        opened={openedDeleteApproved.isOpen}
        onClose={() =>
          setOpenedDeleteApproved({
            id: "",
            isOpen: false,
          })
        }
        leaveId={openedDeleteApproved.id}
      />
    </Paper>
  );
}
